// src/app/services/app-init.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { StoreService } from './store.service';
import { environment } from '../../environments/environment';
import { ConnectionStatusService } from './connection-status.service';
import { EMPTY, filter, from, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { Lang, LOCAL_STORAGE_KEYS } from '../config/general.config';
import { DialogService } from './dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  private keycloakInitialized = false;
  private keycloakUrl = environment.keycloakUrl;

  constructor(
    private translate: TranslateService,
    private keycloakService: KeycloakService,
    private storeService: StoreService,
    private connectionStatusService: ConnectionStatusService,
    private dialogService: DialogService,
    private http: HttpClient,
    private sessionService: SessionService
  ) {
    this.translate.setDefaultLang(Lang.PL);
    this.translate.use(Lang.PL);
  }

  private loadEnvConfig(): Observable<void> {
    if (!environment.production) {
      return of(void 0);
    }
    return this.http.get<{ keycloakUrl: string }>('/env-config.json').pipe(
      tap(config => this.keycloakUrl = config.keycloakUrl),
      map(() => void 0)
    );
  }

  private async initKeycloak(): Promise<boolean> {
    if(this.keycloakInitialized) {
      return await this.keycloakService.isLoggedIn()
    }
    const loggedIn = await this.keycloakService.init({
      config: {
        url: this.keycloakUrl,
        realm: environment.keycloakRealm,
        clientId: environment.keycloakClientId
      },
      initOptions: {
        checkLoginIframe: true,
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html'
      }
    })
    this.keycloakInitialized = true;
    return loggedIn;
  }

  installLoginMonitor() {
    this.connectionStatusService.networkStatus$.pipe(
      filter(isOnline => isOnline),
      switchMap(() => from(this.initKeycloak())),
      switchMap(isLoggedIn => {
        if (!isLoggedIn) {
          return this.dialogService.promptForGoOnline().afterClosed().pipe(
            tap(response => {
              if (response) {
                this.keycloakService.login();
              } else {
                this.connectionStatusService.goOffline();
              }
            })
          );
        }
        else{
          this.sessionService.startSession();
        }
        return EMPTY;
      })
    ).subscribe();
  }

  public initializeApp(): Observable<void> {
    return this.loadEnvConfig().pipe(
      switchMap(() => this.connectionStatusService.networkStatus$.pipe(
        take(1),
        switchMap((isOnline) => isOnline ? this.initOnline() : this.initOffline()),
        tap(() => setTimeout(() => this.installLoginMonitor(), 100))
      ))
    );
  }

  private initOnline(): Observable<void> {
    return from(this.initKeycloak())
    .pipe(
      switchMap((isLoggedIn) => {
        if (isLoggedIn) {
          // this.storeService.setPermissions(this.keycloakService.getUserRoles());
          this.setPermissions(true);
          return from(this.storeService.initApp() as Promise<void>);
        } else {
          return from(this.keycloakService.login());
        }
      })
    )
  }

  private initOffline(): Observable<void>  {
    this.setPermissions(false);
    return from(this.storeService.initApp() as Promise<void>);
  }

  setPermissions(isOnline: boolean){
    if(isOnline){
      const permissions = this.keycloakService.getUserRoles();
      this.storeService.setPermissions(permissions);
      localStorage.setItem(LOCAL_STORAGE_KEYS.permissions, JSON.stringify(permissions));
    }
    else {
      const permissions = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.permissions) || '[]');
      this.storeService.setPermissions(permissions);
    }
  }
}
